import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import VehicleTypeSelector from '../components/Filters/TypeSelector/VehicleTypeSelector';
import PartnerDealersListSelector from '../components/Filters/PartnerDealersList/PartnerDealersListSelector';
import BodyStyle from '../components/Filters/BodyStyle/BodyStyleList';
import DrivetrainSelector from '../components/Filters/Drivetrain/DrivetrainSelector';
import ExteriorSelector from '../components/Filters/Exterior/ExteriorSelector';
import FuelTypeSelector from '../components/Filters/FuelType/FuelTypeSelector';
import GearboxSelector from '../components/Filters/Gearbox/GearboxSelector';
import InteriorSelector from '../components/Filters/Interior/Interior';
import MileageSelector from '../components/Filters/Mileage/MileageSelector';
import ModelFamilySelector from '../components/Filters/ModelFamily/ModelFamilySelector';
import ModelYearSelector from '../components/Filters/ModelYear/ModelYearSelector';
import PowerSelector from '../components/Filters/Power/PowerSelector';
import PriceSelector from '../components/Filters/Price/PriceSelector';
import TrimLineSelector from '../components/Filters/TrimLine/TrimLineSelector';
import WheelSizeSelector from '../components/Filters/WheelSize/WheelSizeSelector';
import useFilterOptions from './useFilterOptions';
import useGetFilters from './useGetFilters';
import { selectAppConfig } from '../redux/appConfigReducer';
import {
  ExposedFiltersTypes,
  NEW_VEHICLE_FILTERS_LIST,
  NEW_VEHICLE_FILTERS_LIST_TIER_3,
  USED_VEHICLE_FILTERS_LIST,
  USED_VEHICLE_FILTERS_LIST_TIER_3,
  USED_COMBINED_FILTERS_LIST_TIER_3,
} from '../constants/initialFilters';
import OtherMakes from '../components/Filters/OtherMakes';
import NonAudiModels from '../components/Filters/NonAudiModels';

type HandleAddFilter = (filterType: string, filterValue: string) => void;
type HandleExposeFilter = (filterType: string) => void;
type HandleChangeRange = (filterType: string, filterValue: number[]) => void;

const useGenerateFilterList = (
  handleAddFilter: HandleAddFilter,
  openedFiltersList: ExposedFiltersTypes,
  handleExposeFilter: HandleExposeFilter,
  handleChangeRange: HandleChangeRange,
) => {
  const { availableFilters, temporaryFilters } = useGetFilters();
  const { listOfDealers, isTier3page, filterGroupsToRender } = useSelector(selectAppConfig);

  const isMultipleDealers = listOfDealers.length > 1;

  const isUsedVehicleFilter = temporaryFilters.vehicleType === 'used';
  const isNonAudiFilterActive = temporaryFilters.nonAudi === 'foreign-brand.yes';
  const isCombinedFiltersActive = temporaryFilters.usedInventoryType === 'has-warranties.combined';
  const usedFiltersListTier3 =
    isCombinedFiltersActive || isNonAudiFilterActive
      ? USED_COMBINED_FILTERS_LIST_TIER_3
      : USED_VEHICLE_FILTERS_LIST_TIER_3;
  // const hasAnyOtherMakeCount = !!availableFilters?.filterResults?.non_audi_brands;

  const {
    vehicleTypeOptions,
    usedInventoryTypeOptions,
    newInventoryTypeOptions,
    modelFamilyOptions,
    bodyTypeOptions,
    trimLineOptions,
    gearBoxOptions,
    drivetrainOptions,
    fuelTypeOptions,
    wheelSizeOptions,
    exteriorColorOptions,
    interiorColorOptions,
    powerOptions,
    mileageOptions,
    priceOptions,
    modelYearOptions,
    nonAudiOption,
    otherMakesOptions,
    nonAudiModelsOptions,
  } = useFilterOptions();

  const showOtherMakesFilter = useMemo(() => {
    const hasAnyOtherMakeCount = !!otherMakesOptions;
    if (!hasAnyOtherMakeCount) return false;
    if (isCombinedFiltersActive || isNonAudiFilterActive) return true;
    return false;
  }, [isCombinedFiltersActive, isNonAudiFilterActive, otherMakesOptions]);

  const showOtherModelsFilter = useMemo(() => {
    const hasAnyOtherModels = !!nonAudiModelsOptions;
    if (!hasAnyOtherModels) return false;
    if (!temporaryFilters.otherMakes?.length) return false;
    if (isCombinedFiltersActive || isNonAudiFilterActive) return true;
    return false;
  }, [
    isCombinedFiltersActive,
    isNonAudiFilterActive,
    nonAudiModelsOptions,
    temporaryFilters.otherMakes?.length,
  ]);

  const hasAnyOtherMakeCount = !!otherMakesOptions;

  const vehicleTypeFilter = useMemo(() => {
    return {
      value: 'vehicleType',
      Component: (
        <VehicleTypeSelector
          filterType="vehicleType"
          vehicleTypeOptions={vehicleTypeOptions}
          availableFilters={availableFilters}
          temporaryFilters={temporaryFilters}
          handleAddFilter={handleAddFilter}
          usedInventoryTypeOptions={usedInventoryTypeOptions}
          newInventoryTypeOptions={newInventoryTypeOptions}
          isTier3page={isTier3page}
          nonAudiOption={nonAudiOption}
        />
      ),
    };
  }, [
    availableFilters,
    handleAddFilter,
    newInventoryTypeOptions,
    temporaryFilters,
    usedInventoryTypeOptions,
    vehicleTypeOptions,
    isTier3page,
    nonAudiOption,
  ]);

  const partnerDealerFilter = useMemo(() => {
    return {
      value: 'partnerDealers',
      Component: isMultipleDealers && (
        <PartnerDealersListSelector
          filterType="partnerDealers"
          filterOptions={listOfDealers}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    handleAddFilter,
    handleExposeFilter,
    isMultipleDealers,
    listOfDealers,
    openedFiltersList,
    temporaryFilters,
  ]);

  const otherMakesFilter = useMemo(() => {
    return {
      value: 'otherMakes',
      Component: showOtherMakesFilter && (
        <OtherMakes
          filterType="otherMakes"
          otherMakesOptions={otherMakesOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    showOtherMakesFilter,
    otherMakesOptions,
    temporaryFilters,
    openedFiltersList,
    handleAddFilter,
    handleExposeFilter,
  ]);

  const nonAudiModelsFilter = useMemo(() => {
    return {
      value: 'nonAudiModels',
      Component: showOtherModelsFilter && (
        <NonAudiModels
          filterType="nonAudiModels"
          nonAudiModelsOptions={nonAudiModelsOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    handleAddFilter,
    handleExposeFilter,
    nonAudiModelsOptions,
    openedFiltersList,
    showOtherModelsFilter,
    temporaryFilters,
  ]);

  const bodyStyleFilter = useMemo(() => {
    return {
      value: 'bodyType',
      Component: (
        <BodyStyle
          filterType="bodyType"
          bodyTypeOptions={bodyTypeOptions}
          availableFilters={availableFilters}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    availableFilters,
    bodyTypeOptions,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    temporaryFilters,
  ]);

  const modelFamilyFilter = useMemo(() => {
    return {
      value: 'modelFamily',
      Component: !isNonAudiFilterActive && (
        <ModelFamilySelector
          filterType="modelFamily"
          modelFamilyOptions={modelFamilyOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    handleAddFilter,
    handleExposeFilter,
    isNonAudiFilterActive,
    modelFamilyOptions,
    openedFiltersList,
    temporaryFilters,
  ]);

  const trimlineFilter = useMemo(() => {
    return {
      value: 'trimLine',
      Component: !isNonAudiFilterActive && (
        <TrimLineSelector
          filterType="trimLine"
          trimLineOptions={trimLineOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    handleAddFilter,
    handleExposeFilter,
    isNonAudiFilterActive,
    openedFiltersList,
    temporaryFilters,
    trimLineOptions,
  ]);

  const exteriorColorFilter = useMemo(() => {
    return {
      value: 'exteriorColor',
      Component: (
        <ExteriorSelector
          filterType="exteriorColor"
          exteriorColorOptions={exteriorColorOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    exteriorColorOptions,
    handleAddFilter,
    handleExposeFilter,
    openedFiltersList,
    temporaryFilters,
  ]);

  const interiorColorFilter = useMemo(() => {
    return {
      value: 'interiorColor',
      Component: (
        <InteriorSelector
          filterType="interiorColor"
          interiorColorOptions={interiorColorOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    handleAddFilter,
    handleExposeFilter,
    interiorColorOptions,
    openedFiltersList,
    temporaryFilters,
  ]);

  const fuelTypeFilter = useMemo(() => {
    return {
      value: 'fuelType',
      Component: (
        <FuelTypeSelector
          filterType="fuelType"
          filterOptions={fuelTypeOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [fuelTypeOptions, handleAddFilter, handleExposeFilter, openedFiltersList, temporaryFilters]);

  const drivetrainFilter = useMemo(() => {
    return {
      value: 'drivetrain',
      Component: (
        <DrivetrainSelector
          filterType="drivetrain"
          filterOptions={drivetrainOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [drivetrainOptions, handleAddFilter, handleExposeFilter, openedFiltersList, temporaryFilters]);

  const wheelSizeFilter = useMemo(() => {
    return {
      value: 'wheelSize',
      Component: (
        <WheelSizeSelector
          filterType="wheelSize"
          filterOptions={wheelSizeOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [handleAddFilter, handleExposeFilter, openedFiltersList, temporaryFilters, wheelSizeOptions]);

  const gearboxFilter = useMemo(() => {
    return {
      value: 'gearBox',
      Component: (
        <GearboxSelector
          filterType="gearBox"
          filterOptions={gearBoxOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleAddFilter={handleAddFilter}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [gearBoxOptions, handleAddFilter, handleExposeFilter, openedFiltersList, temporaryFilters]);

  const modelYearFilter = useMemo(() => {
    return {
      value: 'modelYear',
      Component: (
        <ModelYearSelector
          filterType="modelYear"
          filterOptions={modelYearOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [
    handleChangeRange,
    handleExposeFilter,
    modelYearOptions,
    openedFiltersList,
    temporaryFilters,
  ]);

  const mileageFilter = useMemo(() => {
    return {
      value: 'mileage',
      Component: (
        <MileageSelector
          filterType="mileage"
          filterOptions={mileageOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [handleChangeRange, handleExposeFilter, mileageOptions, openedFiltersList, temporaryFilters]);

  const priceFilter = useMemo(() => {
    return {
      value: 'price',
      Component: (
        <PriceSelector
          filterType="price"
          filterOptions={priceOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [handleChangeRange, handleExposeFilter, openedFiltersList, priceOptions, temporaryFilters]);

  const powerFilter = useMemo(() => {
    return {
      value: 'power',
      Component: (
        <PowerSelector
          filterType="power"
          filterOptions={powerOptions}
          temporaryFilters={temporaryFilters}
          openedFiltersList={openedFiltersList}
          handleChangeRange={handleChangeRange}
          handleExposeFilter={handleExposeFilter}
        />
      ),
    };
  }, [handleChangeRange, handleExposeFilter, openedFiltersList, powerOptions, temporaryFilters]);

  const fullListOfFilters = useMemo(() => {
    return [
      vehicleTypeFilter,
      partnerDealerFilter,
      otherMakesFilter,
      nonAudiModelsFilter,
      bodyStyleFilter,
      modelFamilyFilter,
      trimlineFilter,
      modelYearFilter,
      mileageFilter,
      exteriorColorFilter,
      interiorColorFilter,
      priceFilter,
      fuelTypeFilter,
      drivetrainFilter,
      wheelSizeFilter,
      gearboxFilter,
      powerFilter,
    ];
  }, [
    bodyStyleFilter,
    drivetrainFilter,
    exteriorColorFilter,
    fuelTypeFilter,
    gearboxFilter,
    interiorColorFilter,
    mileageFilter,
    modelFamilyFilter,
    modelYearFilter,
    partnerDealerFilter,
    powerFilter,
    priceFilter,
    trimlineFilter,
    vehicleTypeFilter,
    wheelSizeFilter,
    otherMakesFilter,
    nonAudiModelsFilter,
  ]);

  const listOfFiltersToRender = useMemo(() => {
    if (isTier3page && isUsedVehicleFilter) {
      return fullListOfFilters
        .filter((vehicleFilter) => usedFiltersListTier3.includes(vehicleFilter.value))
        .filter((vehicleFilter) => filterGroupsToRender.includes(vehicleFilter.value));
    }
    if (isTier3page && !isUsedVehicleFilter) {
      return fullListOfFilters
        .filter((vehicleFilter) => NEW_VEHICLE_FILTERS_LIST_TIER_3.includes(vehicleFilter.value))
        .filter((vehicleFilter) => filterGroupsToRender.includes(vehicleFilter.value));
    }

    if (isUsedVehicleFilter)
      return fullListOfFilters.filter((vehicleFilter) =>
        USED_VEHICLE_FILTERS_LIST.includes(vehicleFilter.value),
      );
    return fullListOfFilters.filter((vehicleFilter) =>
      NEW_VEHICLE_FILTERS_LIST.includes(vehicleFilter.value),
    );
  }, [
    filterGroupsToRender,
    fullListOfFilters,
    isTier3page,
    isUsedVehicleFilter,
    usedFiltersListTier3,
  ]);

  return listOfFiltersToRender;
};

export default useGenerateFilterList;
